// LAK & Co — Pick & Mix builder.
// Client-side state machine + cart integration. No dependencies.
(function () {
  'use strict';

  const root = document.querySelector('[data-lak-pm]');
  if (!root) return;

  const STORAGE_KEY = 'lak_pickmix_state';

  const BAG_CONFIG = {
    '500g': {
      scoops: 5,
      variantId: root.dataset.bag500Id,
      price: parseInt(root.dataset.bag500Price, 10),
      priceFormatted: root.dataset.bag500PriceFormatted
    },
    '1kg': {
      scoops: 10,
      variantId: root.dataset.bag1kgId,
      price: parseInt(root.dataset.bag1kgPrice, 10),
      priceFormatted: root.dataset.bag1kgPriceFormatted
    }
  };

  const searchInput = root.querySelector('[data-lak-search]');
  const searchClearBtn = root.querySelector('[data-lak-search-clear]');
  const surpriseBtn = root.querySelector('[data-lak-surprise]');
  const clearBtn = root.querySelector('[data-lak-clear]');
  const progressTextEl = root.querySelector('[data-lak-progress-text]');

  let searchQuery = '';
  let activeDiet = 'all';

  const state = {
    step: 1,
    bagSize: null, // '500g' | '1kg' | null
    contents: {} // { [sweetId]: { id, handle, name, qty } }
  };

  function loadState() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved && typeof saved === 'object') {
        state.step = saved.step || 1;
        state.bagSize = saved.bagSize || null;
        state.contents = saved.contents || {};
      }
    } catch (e) { /* ignore malformed state */ }
  }

  function saveState() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) { /* quota or private mode */ }
  }

  function clearState() {
    state.step = 1;
    state.bagSize = null;
    state.contents = {};
    sessionStorage.removeItem(STORAGE_KEY);
  }

  function totalScoops() {
    return Object.values(state.contents).reduce((sum, s) => sum + s.qty, 0);
  }

  function maxScoops() {
    return state.bagSize ? BAG_CONFIG[state.bagSize].scoops : 0;
  }

  function bagPriceFormatted() {
    return state.bagSize ? BAG_CONFIG[state.bagSize].priceFormatted : '—';
  }

  // ---------- Actions ----------

  function selectBag(size) {
    if (!BAG_CONFIG[size]) return;
    state.bagSize = size;
    state.step = 2;
    state.contents = {};
    saveState();
    render();
  }

  function addSweet(sweetEl) {
    const id = sweetEl.dataset.sweetId;
    if (!id) return;
    if (totalScoops() >= maxScoops()) return;

    const entry = state.contents[id] || {
      id,
      handle: sweetEl.dataset.sweetHandle,
      name: sweetEl.dataset.sweetName,
      qty: 0
    };
    entry.qty += 1;
    state.contents[id] = entry;
    saveState();
    render();
  }

  function removeSweet(sweetEl) {
    const id = sweetEl.dataset.sweetId;
    if (!id) return;
    const entry = state.contents[id];
    if (!entry || entry.qty <= 0) return;
    entry.qty -= 1;
    if (entry.qty === 0) delete state.contents[id];
    saveState();
    render();
  }

  function goToStep(step) {
    const n = parseInt(step, 10);
    if (n < 1 || n > 3) return;
    if (n === 3 && totalScoops() < maxScoops()) return;
    if (n !== 2) {
      const panel = root.querySelector('[data-lak-bag-panel]');
      if (panel) panel.classList.remove('is-expanded');
    }
    state.step = n;
    saveState();
    render();
  }

  async function addToCart() {
    const cfg = BAG_CONFIG[state.bagSize];
    if (!cfg || !cfg.variantId) {
      showError('Bag product not configured.');
      return;
    }

    const items = Object.values(state.contents);
    const contentsString = items
      .map((s) => s.name + ' \u00D7 ' + s.qty)
      .join(', ');
    const sweetIds = items
      .flatMap((s) => Array(s.qty).fill(s.id))
      .join(',');

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: parseInt(cfg.variantId, 10),
            quantity: 1,
            properties: {
              'Bag size': state.bagSize,
              'Contents': contentsString,
              '_sweet_ids': sweetIds
            }
          }]
        })
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        showError(body.description || 'Could not add to cart.');
        return;
      }

      clearState();
      window.location.href = '/cart';
    } catch (e) {
      showError('Network error — please try again.');
    }
  }

  function showError(msg) {
    const el = root.querySelector('[data-lak-error]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }

  function clearError() {
    const el = root.querySelector('[data-lak-error]');
    if (el) { el.textContent = ''; el.hidden = true; }
  }

  // ---------- Rendering ----------

  function render() {
    renderStepVisibility();
    renderSweetQtys();
    renderBagPanel();
    renderReview();
    renderFilters();
  }

  function renderStepVisibility() {
    root.querySelectorAll('[data-lak-step]').forEach((el) => {
      const n = parseInt(el.dataset.lakStep, 10);
      el.hidden = n !== state.step;
    });
    const sizeLabel = state.bagSize || '500g';
    root.querySelectorAll('[data-lak-current-bag-size]').forEach((el) => {
      el.textContent = sizeLabel;
    });
  }

  function renderSweetQtys() {
    root.querySelectorAll('[data-lak-sweet]').forEach((el) => {
      const id = el.dataset.sweetId;
      const qty = (state.contents[id] && state.contents[id].qty) || 0;
      const qtyEl = el.querySelector('[data-lak-sweet-qty]');
      if (qtyEl) qtyEl.textContent = qty;
      el.classList.toggle('is-added', qty > 0);
      const addBtn = el.querySelector('[data-lak-sweet-add]');
      if (addBtn) {
        const full = totalScoops() >= maxScoops();
        const unavailable = el.hasAttribute('data-sweet-unavailable');
        addBtn.disabled = full || unavailable;
      }
    });
  }

  function renderBagPanel() {
    const filled = totalScoops();
    const max = maxScoops();

    // Dynamic progress message
    if (progressTextEl) {
      let msg = 'Pick your first scoop!';
      if (filled === max && max > 0) msg = 'Bag full \u2014 you\u2019re set!';
      else if (filled > 0 && filled === max - 1) msg = 'Just one more scoop!';
      else if (filled > 0) msg = (max - filled) + ' scoops to go!';
      progressTextEl.textContent = msg;
    }

    // Full-bag state
    const bagPanel = root.querySelector('[data-lak-bag-panel]');
    if (bagPanel) bagPanel.classList.toggle('is-full', filled >= max && max > 0);
    if (clearBtn) clearBtn.hidden = filled === 0;

    const pct = max > 0 ? Math.min(100, (filled / max) * 100) : 0;
    const fillEl = root.querySelector('[data-lak-progress-fill]');
    if (fillEl) fillEl.style.width = pct + '%';

    // Slot visualiser
    const slotsEl = root.querySelector('[data-lak-slots]');
    if (slotsEl) {
      slotsEl.innerHTML = '';
      for (let i = 0; i < max; i++) {
        const slot = document.createElement('span');
        slot.className = 'lak-pm__slot' + (i < filled ? ' is-filled' : '');
        slotsEl.appendChild(slot);
      }
    }

    // List
    const listEl = root.querySelector('[data-lak-bag-list]');
    if (listEl) {
      listEl.innerHTML = '';
      Object.values(state.contents).forEach((s) => {
        const li = document.createElement('li');
        li.className = 'lak-pm__bag-list-item';
        li.innerHTML =
          '<span class="lak-pm__bag-list-name">' + escapeHtml(s.name) + '</span>' +
          '<span class="lak-pm__bag-list-qty">\u00D7' + s.qty + '</span>';
        listEl.appendChild(li);
      });
    }

    // Total
    root.querySelectorAll('[data-lak-total-price]').forEach((el) => {
      el.textContent = filled > 0 ? bagPriceFormatted() : '—';
    });

    // Continue button
    root.querySelectorAll('[data-lak-continue]').forEach((btn) => {
      btn.disabled = filled < max;
    });
  }

  function renderReview() {
    root.querySelectorAll('[data-lak-review-size]').forEach((el) => {
      el.textContent = state.bagSize || '—';
    });
    root.querySelectorAll('[data-lak-review-scoops]').forEach((el) => {
      el.textContent = totalScoops();
    });
    root.querySelectorAll('[data-lak-review-total]').forEach((el) => {
      el.textContent = bagPriceFormatted();
    });

    const listEl = root.querySelector('[data-lak-review-list]');
    if (listEl) {
      listEl.innerHTML = '';
      Object.values(state.contents).forEach((s) => {
        const li = document.createElement('li');
        li.className = 'lak-pm__review-item';
        li.innerHTML =
          '<span>' + escapeHtml(s.name) + '</span>' +
          '<span>\u00D7 ' + s.qty + '</span>';
        listEl.appendChild(li);
      });
    }
  }

  function matchesDiet(diet, tags, name) {
    if (diet === 'all') return true;
    if (tags.includes(diet)) return true;
    if (name.includes(diet)) return true;
    if (diet === 'vegetarian') {
      if (tags.includes('vegan') || name.includes('vegan')) return true;
    }
    return false;
  }

  function renderFilters() {
    root.querySelectorAll('[data-lak-sweet]').forEach((el) => {
      const tags = (el.dataset.sweetTags || '').split(',').map(function (t) { return t.trim().toLowerCase(); });
      const name = (el.dataset.sweetName || '').toLowerCase();
      let hide = false;

      if (!matchesDiet(activeDiet, tags, name)) hide = true;

      if (!hide && searchQuery) {
        if (!name.includes(searchQuery)) hide = true;
      }

      el.hidden = hide;
    });
  }

  function surpriseFill() {
    const visible = Array.from(root.querySelectorAll('[data-lak-sweet]'))
      .filter(function (el) { return !el.hidden && !el.hasAttribute('data-sweet-unavailable'); });
    if (visible.length === 0) return;
    state.contents = {};
    const n = maxScoops();
    for (var i = 0; i < n; i++) {
      const pick = visible[Math.floor(Math.random() * visible.length)];
      const id = pick.dataset.sweetId;
      const entry = state.contents[id] || {
        id: id,
        handle: pick.dataset.sweetHandle,
        name: pick.dataset.sweetName,
        qty: 0
      };
      entry.qty += 1;
      state.contents[id] = entry;
    }
    saveState();
    render();
  }

  function escapeHtml(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ---------- Event wiring ----------

  root.addEventListener('click', (e) => {
    const selectBagBtn = e.target.closest('[data-lak-select-bag]');
    if (selectBagBtn) {
      selectBag(selectBagBtn.dataset.bagSize);
      return;
    }

    const addBtn = e.target.closest('[data-lak-sweet-add]');
    if (addBtn) {
      const sweetEl = addBtn.closest('[data-lak-sweet]');
      if (sweetEl) addSweet(sweetEl);
      return;
    }

    const removeBtn = e.target.closest('[data-lak-sweet-remove]');
    if (removeBtn) {
      const sweetEl = removeBtn.closest('[data-lak-sweet]');
      if (sweetEl) removeSweet(sweetEl);
      return;
    }

    const backBtn = e.target.closest('[data-lak-back-to-step]');
    if (backBtn) {
      if (backBtn.dataset.lakBackToStep === '1') {
        const ok = totalScoops() === 0 || window.confirm('Change bag? Your current selections will be cleared.');
        if (!ok) return;
        state.contents = {};
      }
      goToStep(backBtn.dataset.lakBackToStep);
      return;
    }

    const continueBtn = e.target.closest('[data-lak-continue]');
    if (continueBtn) {
      goToStep(3);
      return;
    }

    const cartBtn = e.target.closest('[data-lak-add-to-cart]');
    if (cartBtn) {
      clearError();
      cartBtn.disabled = true;
      cartBtn.textContent = 'Adding…';
      addToCart().finally(() => {
        cartBtn.disabled = false;
        cartBtn.textContent = 'Add to cart';
      });
      return;
    }

    const toggleBtn = e.target.closest('[data-lak-bag-toggle]');
    if (toggleBtn) {
      const panel = root.querySelector('[data-lak-bag-panel]');
      if (!panel) return;
      const expanded = panel.classList.toggle('is-expanded');
      toggleBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      toggleBtn.setAttribute('aria-label', expanded ? 'Hide bag contents' : 'Show bag contents');
      return;
    }

    const dietBtn = e.target.closest('[data-lak-diet]');
    if (dietBtn) {
      root.querySelectorAll('[data-lak-diet]').forEach((b) => b.classList.remove('is-active'));
      dietBtn.classList.add('is-active');
      activeDiet = dietBtn.dataset.lakDiet || 'all';
      renderFilters();
      return;
    }
  });

  // Search input
  if (searchInput) {
    searchInput.addEventListener('input', function (e) {
      searchQuery = (e.target.value || '').toLowerCase().trim();
      if (searchClearBtn) searchClearBtn.hidden = !searchQuery;
      renderFilters();
    });
  }
  if (searchClearBtn) {
    searchClearBtn.addEventListener('click', function () {
      if (searchInput) { searchInput.value = ''; }
      searchQuery = '';
      searchClearBtn.hidden = true;
      renderFilters();
    });
  }


  // Surprise me
  if (surpriseBtn) {
    surpriseBtn.addEventListener('click', surpriseFill);
  }

  // Clear bag
  if (clearBtn) {
    clearBtn.addEventListener('click', function () {
      if (totalScoops() === 0) return;
      if (window.confirm('Clear your bag?')) {
        state.contents = {};
        saveState();
        render();
      }
    });
  }

  // ---------- Init ----------

  loadState();
  // Safety: if restored step references a missing bag, reset
  if (state.step > 1 && !BAG_CONFIG[state.bagSize]) {
    clearState();
  }
  render();
})();
